import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from '../confirmation-dialog/confirmation-dialog.component';
import {
  GsUserService,
  DynamicTranslationService,
  AdminService,
  CharitySearchResult,
  ChangePermissionPayload,
  CharityRole,
  UserCharityRole,
} from '@gs-2.0/core';
import { ToastService } from '../../services/toast.service';

export interface UserPermissions {
  // User Type - single selection
  userType: 'donor' | 'charity' | 'foundation' | null;

  // User Role - single selection
  userRole:
    | 'normal_user'
    | 'project_admin'
    | 'charity_admin'
    | 'champion_admin'
    | 'foundation_admin'
    | 'super_admin'
    | null;

  // Type checkboxes
  waitingRoom: boolean;
  champion: boolean;
  languageEditor: boolean;
}

export interface EditUserDialogData {
  userId: string;
  avatar: string;
  firstName: string;
  lastName: string;
  email: string;
  status: string;
  charity?: string;
  charityUuid?: string;
  rankingScore?: number;
  permissions: UserPermissions;
  userCharityRoles?: UserCharityRole[];
}

export interface EditUserDialogResult {
  action: 'save' | 'reset-password' | 'edit-profile';
  permissions?: UserPermissions;
  charity?: string;
  rankingScore?: number;
}

interface CharityEntry {
  charity: CharitySearchResult | null;
  role: 'project_admin' | 'charity_admin' | '';
  searchQuery: string;
  searchResults: CharitySearchResult[];
  showCharityError: boolean;
  showRoleError: boolean;
}

@Component({
  selector: 'app-edit-user-dialog',
  standalone: false,
  templateUrl: './edit-user-dialog.component.html',
  styleUrls: ['./edit-user-dialog.component.scss'],
})
export class EditUserDialogComponent implements OnInit, OnDestroy {
  charityEntries: CharityEntry[] = [];
  isResettingPassword = false;
  isSaving = false;

  private initialPermissions: UserPermissions | null = null;
  private initialRankingScore: number | undefined;
  private initialCharityEntries: Array<{ charityUuid: string; role: string }> = [];

  /**
   * True when the logged-in user editing this dialog is a charity-scoped editor
   * (GS_ORGANISATION_ADMIN / GS_CHARITY_MANAGER). Such editors may only manage charity
   * assignments that belong to their own userCharityRoles.
   */
  isRestrictedEditor = false;
  private editorCharities: CharitySearchResult[] = [];
  private editorCharityUuids = new Set<string>();

  /**
   * True when the logged-in editor is a Foundation Admin (GS_ADMIN_FOUNDATION) or
   * Super Admin (GS_SUPERADMIN). These editors may assign ANY charity: the full
   * organisation list is loaded once and shown in the charity dropdown (scroll +
   * local search) instead of the type-to-search field.
   */
  isAllCharitiesEditor = false;
  private allCharities: CharitySearchResult[] = [];
  private allCharitiesRequested = false;

  /**
   * True only for GS_SUPERADMIN / GS_ADMIN_FOUNDATION editors. The "Reset Password"
   * and "Edit User Profile" actions are reserved for these elevated admins; other
   * editors (e.g. charity managers / organisation admins) don't see those buttons.
   */
  isElevatedAdmin = false;

  /**
   * TEMP: the Waiting Room type is hidden everywhere and always sent to
   * change-permission as false. Flip back to true to restore the checkbox.
   */
  readonly waitingRoomEnabled = false;

  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<EditUserDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditUserDialogData,
    private gsUserService: GsUserService,
    private toastService: ToastService,
    private translationService: DynamicTranslationService,
    private adminService: AdminService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.initializeCharityEntries();
    this.captureInitialState();
    this.loadEditorScope();
    this.dialogRef.disableClose = true;
    this.dialogRef
      .backdropClick()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.attemptClose());
  }

  /**
   * Resolve the logged-in editor's role and own charities from get-user-info.
   * Only GS_ORGANISATION_ADMIN / GS_CHARITY_MANAGER editors are restricted.
   */
  private loadEditorScope(): void {
    this.gsUserService
      .getUserInfo()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const role = response.response.role;
          this.isRestrictedEditor =
            role === 'GS_ORGANIZATION_ADMIN' ||
            role === 'GS_ORGANISATION_ADMIN' ||
            role === 'GS_CHARITY_MANAGER';

          // Reset Password / Edit User Profile are reserved for super / foundation admins.
          this.isElevatedAdmin = role === 'GS_SUPERADMIN' || role === 'GS_ADMIN_FOUNDATION';

          if (!this.isRestrictedEditor) {
            // Foundation / Super admins get the full organisation list in the
            // charity dropdown. Load it now if the edited user is already a
            // charity manager (section visible on open); otherwise it loads when
            // the editor selects the Charity Manager role.
            this.isAllCharitiesEditor = role === 'GS_SUPERADMIN' || role === 'GS_ADMIN_FOUNDATION';
            if (this.isAllCharitiesEditor && this.isCharityManagerSelected) {
              this.loadAllCharities();
            }
            return;
          }

          // Restricted editors can only assign charities they are a Charity Admin of
          // (userCharityRoles.subRole === 'GS_CHARITY_ADMIN'); other sub-roles are excluded.
          const seen = new Set<string>();
          this.editorCharities = [];
          this.editorCharityUuids = new Set<string>();
          for (const charityRole of response.response.userCharityRoles ?? []) {
            if (
              charityRole.subRole === 'GS_CHARITY_ADMIN' &&
              charityRole.charityUuid &&
              charityRole.charityName &&
              !seen.has(charityRole.charityUuid)
            ) {
              seen.add(charityRole.charityUuid);
              this.editorCharities.push({
                uuid: charityRole.charityUuid,
                name: charityRole.charityName,
              });
              this.editorCharityUuids.add(charityRole.charityUuid);
            }
          }
        },
        error: (error) => {
          console.error('Error loading editor scope for edit-user dialog:', error);
        },
      });
  }

  /** An existing entry whose charity is outside the restricted editor's own charities. */
  isEntryLocked(entry: CharityEntry): boolean {
    return (
      this.isRestrictedEditor && !!entry.charity && !this.editorCharityUuids.has(entry.charity.uuid)
    );
  }

  /** Restricted editors can't retype an already-populated charity name (only add new ones). */
  isEntryInputDisabled(entry: CharityEntry): boolean {
    return this.isRestrictedEditor && !!entry.charity;
  }

  /** Role radios are disabled only for out-of-scope (locked) entries. */
  isEntryRoleDisabled(entry: CharityEntry): boolean {
    return this.isEntryLocked(entry);
  }

  /**
   * Whether the "add charity" button on the last entry should be shown.
   * Restricted editors (GS_ORGANISATION_ADMIN / GS_CHARITY_MANAGER) can only
   * assign charities they belong to. The button is shown only when there are more
   * still-unassigned charities than empty entry cards to hold them — i.e. a new
   * card is actually needed. So it hides when the remaining unassigned charity
   * already has an empty card waiting for it, and reappears once that card is
   * filled while another charity is still unassigned. Other editors are unrestricted.
   */
  get canAddMoreCharityEntries(): boolean {
    if (!this.isRestrictedEditor) {
      return true;
    }
    const assigned = new Set(
      this.charityEntries.filter((e) => e.charity).map((e) => e.charity!.uuid)
    );
    const unassignedCount = this.editorCharities.filter((c) => !assigned.has(c.uuid)).length;
    const emptyCardCount = this.charityEntries.filter((e) => !e.charity).length;
    return unassignedCount > emptyCardCount;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeCharityEntries(): void {
    this.charityEntries = [];

    // Load existing charity roles returned from the get-users API (userCharityRoles)
    const existingRoles = this.data.userCharityRoles || [];
    for (const charityRole of existingRoles) {
      if (!charityRole.charityUuid) continue;
      const name = charityRole.charityName || '';
      this.charityEntries.push({
        charity: { uuid: charityRole.charityUuid, name },
        role: this.mapSubRoleToEntryRole(charityRole.subRole),
        searchQuery: name,
        searchResults: [],
        showCharityError: false,
        showRoleError: false,
      });
    }

    // Fall back to the single charity passed via data when no charity roles were provided
    if (this.charityEntries.length === 0 && this.data.charity && this.data.charityUuid) {
      const role =
        this.data.permissions.userRole === 'project_admin' ||
        this.data.permissions.userRole === 'charity_admin'
          ? (this.data.permissions.userRole as 'project_admin' | 'charity_admin')
          : '';
      this.charityEntries.push({
        charity: { uuid: this.data.charityUuid, name: this.data.charity },
        role,
        searchQuery: this.data.charity,
        searchResults: [],
        showCharityError: false,
        showRoleError: false,
      });
    }

    // Always add one empty entry at the end for "add new"
    this.charityEntries.push({
      charity: null,
      role: '',
      searchQuery: '',
      searchResults: [],
      showCharityError: false,
      showRoleError: false,
    });
  }

  private mapSubRoleToEntryRole(subRole: string): 'project_admin' | 'charity_admin' | '' {
    switch (subRole) {
      case 'GS_CHARITY_ADMIN':
        return 'charity_admin';
      case 'GS_PROJECT_ADMIN':
        return 'project_admin';
      default:
        return '';
    }
  }

  onUserTypeChange(type: 'donor' | 'charity' | 'foundation'): void {
    this.data.permissions.userType = type;
    this.syncPermissionsToUserType();
  }

  /** User Role radio values available for the currently selected user type. */
  get availableRoleValues(): string[] {
    switch (this.data.permissions.userType) {
      case 'donor':
        return ['normal_user', 'champion_admin'];
      case 'charity':
        return ['normal_user', 'charity_manager'];
      case 'foundation':
        return ['normal_user', 'foundation_admin', 'super_admin'];
      default:
        return [];
    }
  }

  /** Type checkbox fields available for the currently selected user type. */
  get availableTypeFields(): Array<'waitingRoom' | 'champion' | 'languageEditor'> {
    switch (this.data.permissions.userType) {
      case 'donor':
        return ['champion', 'waitingRoom'];
      case 'charity':
        return ['waitingRoom'];
      case 'foundation':
        return ['waitingRoom', 'languageEditor'];
      default:
        return [];
    }
  }

  /** Whether a User Role radio (by its value attribute) is shown for the current user type. */
  isRoleAvailable(role: string): boolean {
    return this.availableRoleValues.includes(role);
  }

  /** Whether a Type checkbox is shown for the current user type. */
  isTypeFieldAvailable(field: 'waitingRoom' | 'champion' | 'languageEditor'): boolean {
    if (field === 'waitingRoom' && !this.waitingRoomEnabled) return false;
    return this.availableTypeFields.includes(field);
  }

  /** Whether the Type column still has at least one visible checkbox. */
  get hasVisibleTypeFields(): boolean {
    return this.availableTypeFields.some((field) => this.isTypeFieldAvailable(field));
  }

  /** Clear any role/type selections that aren't valid for the current user type. */
  private syncPermissionsToUserType(): void {
    // The 'charity_manager' radio represents the project_admin / charity_admin sub-roles.
    const currentRole = this.data.permissions.userRole;
    const roleStillValid =
      currentRole === 'project_admin' || currentRole === 'charity_admin'
        ? this.isRoleAvailable('charity_manager')
        : !!currentRole && this.isRoleAvailable(currentRole);
    if (!roleStillValid) {
      this.data.permissions.userRole = null;
    }

    if (!this.isTypeFieldAvailable('waitingRoom')) this.data.permissions.waitingRoom = false;
    if (!this.isTypeFieldAvailable('champion')) this.data.permissions.champion = false;
    if (!this.isTypeFieldAvailable('languageEditor')) this.data.permissions.languageEditor = false;
  }

  get isCharityManagerSelected(): boolean {
    return (
      this.data.permissions.userRole === 'project_admin' ||
      this.data.permissions.userRole === 'charity_admin'
    );
  }

  onUserRoleChange(role: string): void {
    if (role === 'charity_manager') {
      // Keep the existing specific role if already set, otherwise default to project_admin
      if (
        this.data.permissions.userRole !== 'project_admin' &&
        this.data.permissions.userRole !== 'charity_admin'
      ) {
        this.data.permissions.userRole = 'project_admin';
      }
      // Foundation / Super admin editors load the full organisation list once so
      // the charity dropdown can show every charity to scroll/search through.
      if (this.isAllCharitiesEditor) {
        this.loadAllCharities();
      }
    } else {
      this.data.permissions.userRole = role as UserPermissions['userRole'];
    }
  }

  onPermissionTypeChange(
    field: 'waitingRoom' | 'champion' | 'languageEditor',
    checked: boolean
  ): void {
    this.data.permissions[field] = checked;
  }

  /**
   * Show the available charities as soon as the charity input is focused, without
   * waiting for the user to type. Only applies to restricted editors
   * (GS_ORGANISATION_ADMIN / GS_CHARITY_MANAGER), whose options come from their own
   * userCharityRoles. An empty query returns every charity not already assigned in
   * another entry.
   */
  onEntryCharityFocus(index: number): void {
    const entry = this.charityEntries[index];
    if (!entry || this.isEntryInputDisabled(entry)) return;
    if (this.isRestrictedEditor) {
      entry.searchResults = this.filterEditorCharities(entry.searchQuery, index);
    } else if (this.isAllCharitiesEditor) {
      this.loadAllCharities();
      entry.searchResults = this.filterAllCharities(entry.searchQuery, index);
    }
  }

  onEntryCharityInput(index: number, value: string): void {
    const entry = this.charityEntries[index];
    entry.searchQuery = value;
    entry.showCharityError = false;
    if (entry.charity && value !== entry.charity.name) {
      entry.charity = null;
    }
    if (this.isRestrictedEditor) {
      // Restricted editors can only add charities from their own userCharityRoles.
      entry.searchResults = this.filterEditorCharities(value, index);
    } else if (this.isAllCharitiesEditor) {
      // Foundation / Super admins filter the pre-loaded organisation list locally.
      entry.searchResults = this.filterAllCharities(value, index);
    } else if (value.trim().length > 0) {
      this.searchCharitiesForEntry(index, value);
    } else {
      entry.searchResults = [];
    }
  }

  /**
   * Filter the editor's own charities by query (case-insensitive), excluding charities
   * already assigned in other entries.
   */
  private filterEditorCharities(query: string, index: number): CharitySearchResult[] {
    return this.filterCharityPool(this.editorCharities, query, index);
  }

  /**
   * Filter the full organisation list (foundation/super admins) by query
   * (case-insensitive), excluding charities already assigned in other entries.
   */
  private filterAllCharities(query: string, index: number): CharitySearchResult[] {
    return this.filterCharityPool(this.allCharities, query, index);
  }

  /** Shared filter: charities in `pool` matching `query`, minus those taken by other entries. */
  private filterCharityPool(
    pool: CharitySearchResult[],
    query: string,
    index: number
  ): CharitySearchResult[] {
    const needle = query.trim().toLowerCase();
    const taken = new Set<string>();
    this.charityEntries.forEach((e, i) => {
      if (i !== index && e.charity) taken.add(e.charity.uuid);
    });
    return pool.filter((c) => !taken.has(c.uuid) && c.name.toLowerCase().includes(needle));
  }

  /**
   * Load every charity (organisation) once via the entities API
   * (/v1/admin/entities/ORGANISATION?searchInput=) for foundation/super admin editors.
   */
  private loadAllCharities(): void {
    if (this.allCharitiesRequested) return;
    this.allCharitiesRequested = true;

    this.adminService
      .searchCharities('')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.allCharities = response.response || [];
        },
        error: (error) => {
          console.error('Error loading all charities for foundation/super admin editor:', error);
          this.allCharities = [];
          this.allCharitiesRequested = false; // allow a retry
        },
      });
  }

  private searchCharitiesForEntry(index: number, query: string): void {
    this.adminService
      .searchCharities(query)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (this.charityEntries[index]) {
            this.charityEntries[index].searchResults = response.response || [];
          }
        },
        error: () => {
          if (this.charityEntries[index]) {
            this.charityEntries[index].searchResults = [];
          }
        },
      });
  }

  onEntryCharitySelected(index: number, charity: CharitySearchResult): void {
    const entry = this.charityEntries[index];

    // Prevent assigning the same charity (by UUID) more than once
    const isDuplicate = this.charityEntries.some(
      (existing, i) => i !== index && existing.charity?.uuid === charity.uuid
    );
    if (isDuplicate) {
      this.toastService.info(
        this.translationService.instant('settings.users.edit_dialog.charity_already_assigned')
      );
      // Do not add the duplicate charity to the input field
      entry.charity = null;
      entry.searchQuery = '';
      entry.searchResults = [];
      return;
    }

    entry.charity = charity;
    entry.searchQuery = charity.name;
    entry.searchResults = [];
    entry.showCharityError = false;
  }

  onEntryRoleChange(index: number, role: 'project_admin' | 'charity_admin'): void {
    this.charityEntries[index].role = role;
    this.charityEntries[index].showRoleError = false;
  }

  addCharityEntry(): void {
    const last = this.charityEntries[this.charityEntries.length - 1];
    let hasError = false;
    if (!last.charity) {
      last.showCharityError = true;
      hasError = true;
    }
    if (!last.role) {
      last.showRoleError = true;
      hasError = true;
    }
    if (hasError) return;
    this.charityEntries.push({
      charity: null,
      role: '',
      searchQuery: '',
      searchResults: [],
      showCharityError: false,
      showRoleError: false,
    });
  }

  deleteCharityEntry(index: number): void {
    this.charityEntries.splice(index, 1);
  }

  get statusClass(): string {
    switch (this.data.status.toLowerCase()) {
      case 'complete':
        return 'status-complete';
      case 'incomplete':
        return 'status-incomplete';
      case 'draft':
        return 'status-draft';
      default:
        return '';
    }
  }

  onClose(): void {
    this.attemptClose();
  }

  private captureInitialState(): void {
    this.initialPermissions = { ...this.data.permissions };
    this.initialRankingScore = this.data.rankingScore;
    this.initialCharityEntries = this.charityEntries
      .filter((e) => !!e.charity)
      .map((e) => ({ charityUuid: e.charity!.uuid, role: e.role }));
  }

  private isDirty(): boolean {
    if (!this.initialPermissions) return false;
    const p = this.data.permissions;
    const snap = this.initialPermissions;
    if (
      p.userType !== snap.userType ||
      p.userRole !== snap.userRole ||
      p.waitingRoom !== snap.waitingRoom ||
      p.champion !== snap.champion ||
      p.languageEditor !== snap.languageEditor ||
      this.data.rankingScore !== this.initialRankingScore
    ) {
      return true;
    }
    const current = this.charityEntries
      .filter((e) => !!e.charity)
      .map((e) => ({ charityUuid: e.charity!.uuid, role: e.role }));
    if (current.length !== this.initialCharityEntries.length) return true;
    return current.some(
      (e, i) =>
        e.charityUuid !== this.initialCharityEntries[i].charityUuid ||
        e.role !== this.initialCharityEntries[i].role
    );
  }

  private attemptClose(): void {
    if (!this.isDirty()) {
      this.dialogRef.close();
      return;
    }
    const warningRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: 'settings.users.edit_dialog.unsaved_changes',
        actionType: 'custom',
        confirmButtonText: 'settings.users.edit_dialog.discard',
        cancelButtonText: 'settings.users.edit_dialog.continue_editing',
      } as ConfirmationDialogData,
      width: '400px',
    });
    warningRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.dialogRef.close();
        }
      });
  }

  onResetPassword(): void {
    this.isResettingPassword = true;

    this.gsUserService
      .resetPassword(this.data.email)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isResettingPassword = false;
        })
      )
      .subscribe({
        next: () => {
          const successMessage = this.translationService.instant(
            'An email has been sent with instructions for the user to reset his password.'
          );
          this.toastService.success(successMessage);
        },
        error: (error) => {
          console.error('Error resetting password:', error);
          const errorMessage = this.translationService.instant('toast.error_resetting_password');
          this.toastService.error(errorMessage);
        },
      });
  }

  onEditUserProfile(): void {
    this.dialogRef.close({
      action: 'edit-profile',
    } as EditUserDialogResult);
  }

  onSave(): void {
    if (this.isSaving) return;

    // Validate entries that have partial data
    let hasError = false;
    for (const entry of this.charityEntries) {
      if (entry.charity || entry.role) {
        if (!entry.charity) {
          entry.showCharityError = true;
          hasError = true;
        }
        if (!entry.role) {
          entry.showRoleError = true;
          hasError = true;
        }
      }
    }
    if (hasError) return;

    const filledEntries = this.charityEntries.filter((e) => e.charity && e.role);
    const firstFilled = filledEntries[0];

    this.isSaving = true;

    const userType = this.mapUserTypeToApi(this.data.permissions.userType);
    // Charity Manager roles take their value from the per-charity entry; every other
    // role (Normal User, Champion Admin, Foundation Admin, Super Admin) comes from the
    // top-level User Role selection.
    const newRole = this.isCharityManagerSelected
      ? this.mapUserRoleToApi(firstFilled?.role || null)
      : this.mapUserRoleToApi(this.data.permissions.userRole);

    // Every filled charity entry card becomes a charityRole — both the existing/primary
    // selection and any additional cards added via the "+" button.
    const charityRoles: CharityRole[] = filledEntries.map((entry) => ({
      subRole: this.mapEntryRoleToSubRole(entry.role),
      charityUuid: entry.charity!.uuid,
    }));

    const payload: ChangePermissionPayload = {
      userUuid: this.data.userId,
      userType: userType,
      entityUuid: firstFilled?.charity?.uuid || this.data.charityUuid || null,
      newRole: newRole,
      champion: this.data.permissions.champion || false,
      userVetted: false,
      // TEMP: Waiting Room is hidden in the UI — always saved as off.
      waitingRoom: false,
      languageEditor: this.data.permissions.languageEditor || false,
      rankingScore: this.data.rankingScore || 0,
      charityRoles: charityRoles,
    };

    this.gsUserService
      .changeUserPermissions(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSaving = false;
        })
      )
      .subscribe({
        next: () => {
          this.toastService.success('Saved successfully!');
          this.dialogRef.close({
            action: 'save',
            permissions: {
              userType: this.data.permissions.userType,
              userRole: (firstFilled?.role ||
                this.data.permissions.userRole) as UserPermissions['userRole'],
              waitingRoom: false,
              champion: this.data.permissions.champion,
              languageEditor: this.data.permissions.languageEditor,
            },
            charity: firstFilled?.charity?.name || this.data.charity,
            rankingScore: this.data.rankingScore || 0,
          } as EditUserDialogResult);
        },
        error: (error) => {
          console.error('Error saving user permissions:', error);
          const errorMessage = this.translationService.instant('toast.error');
          this.toastService.error(errorMessage);
        },
      });
  }

  private mapUserTypeToApi(userType: string | null): string {
    const typeMap: { [key: string]: string } = {
      donor: 'DONOR',
      charity: 'ORGANISATION',
      foundation: 'FOUNDATION',
    };
    return userType ? typeMap[userType] || 'DONOR' : 'DONOR';
  }

  private mapEntryRoleToSubRole(role: 'project_admin' | 'charity_admin' | ''): string {
    return role === 'charity_admin' ? 'GS_CHARITY_ADMIN' : 'GS_PROJECT_ADMIN';
  }

  private mapUserRoleToApi(userRole: string | null): string {
    const roleMap: { [key: string]: string } = {
      normal_user: 'GS_USER',
      project_admin: 'GS_PROJECT_MANAGER',
      charity_admin: 'GS_ORGANIZATION_ADMIN',
      champion_admin: 'GS_ADMIN_CHAMPION',
      foundation_admin: 'GS_FOUNDATION_ADMIN',
      super_admin: 'GS_SUPERADMIN',
    };
    return userRole ? roleMap[userRole] || 'GS_USER' : 'GS_USER';
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = 'https://fdi-files.s3.amazonaws.com/public/userAvatar.jpg';
  }
}
